import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  UNSAFE_NavigationContext as NavigationContext,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { createFilterOptions } from '@mui/material/Autocomplete'
import {
  AddPhotoAlternate,
  DeleteOutline,
  Save,
  Send,
  Visibility,
} from '@mui/icons-material'
import apiService from '../services/ApiService'
import { useAuth } from '../context/AuthContext'
import LexicalBlogEditor from './LexicalBlogEditor'
import PreviewBlogModal from './PreviewBlogModal'

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
}

const LOCAL_DRAFT_KEY_PREFIX = 'blog_writer_local_draft::'
const LEGACY_LOCAL_DRAFT_KEY = 'blog_writer_local_draft'
const LOCAL_DRAFT_ASSET_KEY = 'blog_writer_asset_namespace'
const LOCAL_DRAFT_SCHEMA_VERSION = 2
const DRAFT_TIMESTAMP_TOLERANCE_MS = 1000
const COVER_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const COVER_MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024
const COVER_MIN_WIDTH = 1200
const COVER_MIN_HEIGHT = 675
const TITLE_MAX_CHARS = 220
const SUBTITLE_MAX_CHARS = 320
const DESCRIPTION_MAX_CHARS = 600
const MIN_CONTENT_CHARS_FOR_REVIEW = 100
const GOOGLE_FONTS_STYLESHEET_URL =
  'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Inter:wght@400;500;600;700&family=Lora:wght@400;500;600;700&family=Merriweather:wght@400;700&family=Montserrat:wght@400;500;600;700&family=Nunito+Sans:wght@400;600;700&family=Playfair+Display:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Roboto+Slab:wght@400;500;600;700&family=Source+Sans+3:wght@400;500;600;700&family=Ubuntu:wght@400;500;700&display=swap'
const ADD_CATEGORY_TOKEN = '__add__::'
const categoryFilterOptions = createFilterOptions()

const createDraftAssetNamespace = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return `draft-${crypto.randomUUID()}`
  }
  return `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const createInitialForm = (authorName = '') => ({
  title: '',
  subtitle: '',
  description: '',
  excerpt: '',
  category: '',
  author: authorName || '',
  imageUrl: '',
  metaKeywords: [],
  content: '',
})

const toEpochMillis = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? 0 : parsed
  }
  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0, nanosecond = 0] =
      value
    if (
      !Number.isFinite(year) ||
      !Number.isFinite(month) ||
      !Number.isFinite(day)
    ) {
      return 0
    }
    return Date.UTC(
      year,
      month - 1,
      day,
      hour,
      minute,
      second,
      Math.floor((Number(nanosecond) || 0) / 1000000),
    )
  }
  return 0
}

const themedTextFieldSx = {
  '& .MuiInputLabel-root': {
    color: '#5b6172',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: themeColors.primary,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#cfd6e0',
    },
    '&:hover fieldset': {
      borderColor: themeColors.hover,
    },
    '&.Mui-focused fieldset': {
      borderColor: themeColors.primary,
      borderWidth: 2,
    },
  },
}

const useNavigationBlocker = (when, onBlock) => {
  const { navigator } = useContext(NavigationContext)

  useEffect(() => {
    if (!when || !navigator?.block) {
      return undefined
    }

    const unblock = navigator.block((tx) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock()
          tx.retry()
        },
      }
      onBlock(autoUnblockingTx)
    })

    return unblock
  }, [navigator, onBlock, when])
}

const BlogWriter = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const coverImageInputRef = useRef(null)
  const autosaveTimerRef = useRef(null)
  const editorStateJsonRef = useRef(null)
  const formRef = useRef(null)
  const draftIdRef = useRef(null)
  const draftAssetNamespaceRef = useRef('')
  const lastInitializedLocationKeyRef = useRef('')
  const dirtyVersionRef = useRef(0)

  const defaultAuthorName = useMemo(
    () => user?.name?.trim() || '',
    [user?.name],
  )
  const [form, setForm] = useState(() => {
    const initial = createInitialForm(defaultAuthorName)
    formRef.current = initial
    return initial
  })
  const [keywordInput, setKeywordInput] = useState('')
  const [categoryInputValue, setCategoryInputValue] = useState('')
  const [categories, setCategories] = useState([])
  const [draftId, setDraftId] = useState(null)
  const [draftAssetNamespace, setDraftAssetNamespace] = useState('')
  const [initialEditorStateJson, setInitialEditorStateJson] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [uploadingInlineImage, setUploadingInlineImage] = useState(false)
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false)
  const [coverLocalPreviewUrl, setCoverLocalPreviewUrl] = useState('')
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false)
  const [blockedTx, setBlockedTx] = useState(null)
  const [formErrors, setFormErrors] = useState({})
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [autosaveState, setAutosaveState] = useState('idle')
  const [refreshTick, setRefreshTick] = useState(0)
  const [previewOpen, setPreviewOpen] = useState(false)

  const requestedBlogId = useMemo(() => {
    const raw = searchParams.get('blogId')
    return raw ? raw.trim() : ''
  }, [searchParams])

  useEffect(() => {
    draftIdRef.current = draftId
  }, [draftId])

  useEffect(() => {
    draftAssetNamespaceRef.current = draftAssetNamespace
  }, [draftAssetNamespace])

  const getPlainTextLength = useCallback((html) => {
    if (!html) {
      return 0
    }

    if (typeof document !== 'undefined') {
      const container = document.createElement('div')
      container.innerHTML = html
      const text = (container.textContent || container.innerText || '')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      return text.length
    }

    return String(html)
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim().length
  }, [])

  const contentTextLength = useMemo(
    () => getPlainTextLength(form.content),
    [form.content, getPlainTextLength],
  )
  const contentReviewStatusText = `Content for review: ${contentTextLength}/${MIN_CONTENT_CHARS_FOR_REVIEW} minimum characters.`

  const formatLastSaved = useCallback((timestamp) => {
    if (!timestamp) return ''
    const now = Date.now()
    const diff = Math.floor((now - timestamp) / 1000)

    if (diff < 10) return 'just now'
    if (diff < 60) return `${diff} seconds ago`
    if (diff < 3600) {
      const minutes = Math.floor(diff / 60)
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
    }

    const date = new Date(timestamp)
    const hours = date.getHours()
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours % 12 || 12
    return `at ${displayHours}:${minutes} ${ampm}`
  }, [])

  const autosaveStatusText = useMemo(() => {
    const timeInfo = lastSavedAt
      ? ` Last saved ${formatLastSaved(lastSavedAt)}.`
      : ''

    if (autosaveState === 'saving') {
      return 'Auto-saving your latest changes...'
    }
    if (autosaveState === 'pending' || dirty) {
      return `Autosave is on. Your changes will be saved in a few seconds.${timeInfo}`
    }
    if (autosaveState === 'error') {
      return `Autosave could not save your latest changes. Please use Save Draft.${timeInfo}`
    }

    return `Your draft is saved automatically.${timeInfo}`
  }, [autosaveState, dirty, formatLastSaved, lastSavedAt, refreshTick])

  // Force re-render every 10 seconds to update relative time display
  useEffect(() => {
    if (!lastSavedAt) return
    const interval = setInterval(() => {
      setRefreshTick((prev) => prev + 1)
    }, 10000)
    return () => clearInterval(interval)
  }, [lastSavedAt])

  useEffect(() => {
    if (!defaultAuthorName) {
      return
    }

    setForm((prev) => {
      if (prev.author && prev.author.trim()) {
        return prev
      }
      return {
        ...prev,
        author: defaultAuthorName,
      }
    })
  }, [defaultAuthorName])

  useNavigationBlocker(
    dirty,
    useCallback((tx) => {
      setBlockedTx(tx)
      setLeaveDialogOpen(true)
    }, []),
  )

  useEffect(() => {
    const beforeUnloadHandler = (event) => {
      if (!dirty) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnloadHandler)
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler)
  }, [dirty])

  useEffect(
    () => () => {
      if (coverLocalPreviewUrl && coverLocalPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(coverLocalPreviewUrl)
      }
    },
    [coverLocalPreviewUrl],
  )

  useEffect(() => {
    setCategoryInputValue(form.category || '')
  }, [form.category])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined
    }

    const existing = document.getElementById('blog-writer-google-fonts')
    if (existing) {
      return undefined
    }

    const link = document.createElement('link')
    link.id = 'blog-writer-google-fonts'
    link.rel = 'stylesheet'
    link.href = GOOGLE_FONTS_STYLESHEET_URL
    document.head.appendChild(link)

    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link)
      }
    }
  }, [])

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity })
  }, [])

  const getBackendErrorMessage = useCallback(
    (error) =>
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.data?.errorMessage ||
      error?.message ||
      '',
    [],
  )

  const isMissingCurrentUserDraftError = useCallback(
    (error) => {
      const status = error?.response?.status
      if (status === 404) {
        return true
      }
      const message = getBackendErrorMessage(error).toLowerCase()
      return (
        message.includes('not found for current user') ||
        message.includes('draft not found') ||
        message.includes('blog not found')
      )
    },
    [getBackendErrorMessage],
  )

  const getLocalDraftStorageKey = useCallback((contextId) => {
    if (!contextId || typeof contextId !== 'string') {
      return ''
    }
    const normalizedContextId = contextId.trim()
    if (!normalizedContextId) {
      return ''
    }
    return `${LOCAL_DRAFT_KEY_PREFIX}${normalizedContextId}`
  }, [])

  const getCurrentDraftContextId = useCallback(() => {
    if (
      requestedBlogId &&
      typeof requestedBlogId === 'string' &&
      requestedBlogId.trim()
    ) {
      return requestedBlogId.trim()
    }
    const activeDraftId = draftIdRef.current
    if (
      activeDraftId &&
      typeof activeDraftId === 'string' &&
      activeDraftId.trim()
    ) {
      return activeDraftId.trim()
    }
    const activeDraftNamespace = draftAssetNamespaceRef.current
    if (
      activeDraftNamespace &&
      typeof activeDraftNamespace === 'string' &&
      activeDraftNamespace.trim()
    ) {
      return activeDraftNamespace.trim()
    }
    const activeAsset = localStorage.getItem(LOCAL_DRAFT_ASSET_KEY)
    if (activeAsset && typeof activeAsset === 'string' && activeAsset.trim()) {
      return activeAsset.trim()
    }
    return ''
  }, [requestedBlogId])

  const removeLocalDraftSnapshot = useCallback(
    (contextId) => {
      const key = getLocalDraftStorageKey(contextId)
      if (key) {
        localStorage.removeItem(key)
      }
    },
    [getLocalDraftStorageKey],
  )

  const clearLocalDraftState = useCallback(
    (contextId = '') => {
      const resolvedContextId =
        (contextId && contextId.trim()) || getCurrentDraftContextId()
      if (resolvedContextId) {
        removeLocalDraftSnapshot(resolvedContextId)
      }
      localStorage.removeItem(LOCAL_DRAFT_ASSET_KEY)
      localStorage.removeItem(LEGACY_LOCAL_DRAFT_KEY)
      editorStateJsonRef.current = null
    },
    [getCurrentDraftContextId, removeLocalDraftSnapshot],
  )

  const readLocalDraftSnapshot = useCallback(
    (contextId = '') => {
      const resolvedContextId =
        (contextId && contextId.trim()) || getCurrentDraftContextId()
      if (!resolvedContextId) {
        return null
      }
      const storageKey = getLocalDraftStorageKey(resolvedContextId)
      if (!storageKey) {
        return null
      }
      const raw = localStorage.getItem(storageKey)
      if (!raw) {
        return null
      }
      try {
        const parsed = JSON.parse(raw)
        return parsed && typeof parsed === 'object' ? parsed : null
      } catch (error) {
        console.warn('Failed to parse localStorage draft:', error)
        return null
      }
    },
    [getCurrentDraftContextId, getLocalDraftStorageKey],
  )

  const toLocalCachePayload = useCallback(
    (draft) => ({
      title: draft?.title || '',
      subtitle: draft?.subtitle || '',
      description: draft?.description || '',
      excerpt: draft?.excerpt || '',
      category: draft?.category || '',
      author: draft?.author || defaultAuthorName,
      imageUrl: draft?.imageUrl || '',
      metaKeywords: Array.isArray(draft?.metaKeywords)
        ? draft.metaKeywords
        : [],
      content: draft?.content || '',
    }),
    [defaultAuthorName],
  )

  const reconcileServerAndLocalDraft = useCallback(
    (serverDraft, localDraft) => {
      if (!serverDraft || !localDraft || localDraft?.id !== serverDraft?.id) {
        return {
          useLocalDraft: false,
          useLocalEditorState: false,
          notice: '',
        }
      }

      const localSavedAt = Number(localDraft.savedAt) || 0
      const localLastSyncedAt = Number(localDraft.lastSyncedAt) || 0
      const localHasUnsyncedChanges =
        localSavedAt > 0 &&
        (localLastSyncedAt <= 0 ||
          localSavedAt > localLastSyncedAt + DRAFT_TIMESTAMP_TOLERANCE_MS)

      const serverUpdatedAt = toEpochMillis(serverDraft.updatedDate)
      const snapshotServerUpdatedAt = toEpochMillis(localDraft.serverUpdatedAt)
      const serverAdvancedSinceSnapshot =
        serverUpdatedAt > 0 &&
        snapshotServerUpdatedAt > 0 &&
        serverUpdatedAt > snapshotServerUpdatedAt + DRAFT_TIMESTAMP_TOLERANCE_MS

      if (localHasUnsyncedChanges && serverAdvancedSinceSnapshot) {
        if (localSavedAt >= serverUpdatedAt) {
          return {
            useLocalDraft: true,
            useLocalEditorState: true,
            notice:
              'Recovered unsaved local draft changes. Local copy is newer than server.',
          }
        }
        return {
          useLocalDraft: false,
          useLocalEditorState: false,
          notice: 'Loaded newer server draft changes from another session.',
        }
      }

      if (localHasUnsyncedChanges) {
        return {
          useLocalDraft: true,
          useLocalEditorState: true,
          notice: '',
        }
      }

      const serverOnlyChangedMetadata =
        localDraft.content != null &&
        serverDraft.content != null &&
        localDraft.content === serverDraft.content

      const useLocalEditorState =
        !!localDraft.editorStateJson &&
        (serverOnlyChangedMetadata ||
          !serverUpdatedAt ||
          !snapshotServerUpdatedAt ||
          Math.abs(serverUpdatedAt - snapshotServerUpdatedAt) <=
            DRAFT_TIMESTAMP_TOLERANCE_MS)

      return {
        useLocalDraft: false,
        useLocalEditorState,
        notice: '',
      }
    },
    [],
  )

  const applyLocalDraft = useCallback(
    (draft) => {
      if (!draft) return
      const normalizedDraft = {
        title: draft.title || '',
        subtitle: draft.subtitle || '',
        description: draft.description || '',
        excerpt: draft.excerpt || '',
        category: draft.category || '',
        author: draft.author || defaultAuthorName,
        imageUrl: draft.imageUrl || '',
        metaKeywords: Array.isArray(draft.metaKeywords)
          ? draft.metaKeywords
          : [],
        content: draft.content || '',
      }
      setForm((prev) => {
        const next = { ...prev, ...normalizedDraft }
        formRef.current = next
        return next
      })
      setInitialEditorStateJson(draft.editorStateJson || null)
      if (draft.id) {
        setDraftId(draft.id)
      }
      if (draft.savedAt) {
        setLastSavedAt(draft.savedAt)
      }
    },
    [defaultAuthorName],
  )

  const persistLocalDraft = useCallback(
    (nextForm, id = null, options = {}) => {
      const activeContextId = getCurrentDraftContextId()
      let targetContextId =
        (id && typeof id === 'string' && id.trim()) || activeContextId

      if (!targetContextId) {
        targetContextId = createDraftAssetNamespace()
        localStorage.setItem(LOCAL_DRAFT_ASSET_KEY, targetContextId)
        setDraftAssetNamespace(targetContextId)
      }

      const existingSnapshot = readLocalDraftSnapshot(targetContextId)
      const now = Number(options.savedAt) || Date.now()
      const previouslySyncedAt = Number(existingSnapshot?.lastSyncedAt) || 0
      const synced = Boolean(options.synced)
      const resolvedId = id || existingSnapshot?.id || null
      const resolvedLastSyncedAt = synced
        ? now
        : Number(options.lastSyncedAt) || previouslySyncedAt || null
      const resolvedServerUpdatedAt =
        options.serverUpdatedAt !== undefined
          ? options.serverUpdatedAt
          : existingSnapshot?.serverUpdatedAt || null

      const storageKey = getLocalDraftStorageKey(targetContextId)
      if (storageKey) {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            schemaVersion: LOCAL_DRAFT_SCHEMA_VERSION,
            ...nextForm,
            id: resolvedId,
            contextId: targetContextId,
            editorStateJson:
              options.editorStateJson !== undefined
                ? options.editorStateJson
                : editorStateJsonRef.current,
            savedAt: now,
            lastSyncedAt: resolvedLastSyncedAt,
            serverUpdatedAt: resolvedServerUpdatedAt,
          }),
        )
      }

      if (id && activeContextId && activeContextId !== id) {
        removeLocalDraftSnapshot(activeContextId)
      }
      localStorage.removeItem(LEGACY_LOCAL_DRAFT_KEY)
    },
    [
      getCurrentDraftContextId,
      getLocalDraftStorageKey,
      readLocalDraftSnapshot,
      removeLocalDraftSnapshot,
    ],
  )

  useEffect(() => {
    const initializeWriter = async () => {
      setLoading(true)
      const isNewLocationVisit =
        lastInitializedLocationKeyRef.current !== location.key
      if (isNewLocationVisit) {
        lastInitializedLocationKeyRef.current = location.key
      }

      const startFresh =
        sessionStorage.getItem('blog_writer_start_fresh') === 'true'
      try {
        if (startFresh) {
          sessionStorage.removeItem('blog_writer_start_fresh')
        }
        localStorage.removeItem(LEGACY_LOCAL_DRAFT_KEY)

        const categoriesResponse = await apiService
          .getAllCategories()
          .catch(() => [])
        const categoryList = Array.isArray(categoriesResponse)
          ? categoriesResponse
          : []
        setCategories(categoryList)

        if (requestedBlogId) {
          const myBlog = await apiService
            .getMyBlogById(requestedBlogId, {
              bypassCache: true,
              skipCache: true,
            })
            .catch(() => null)
          if (!myBlog) {
            clearLocalDraftState(requestedBlogId)
            setDraftId(null)
            const nextForm = createInitialForm(defaultAuthorName)
            setForm(nextForm)
            formRef.current = nextForm
            setInitialEditorStateJson(null)
            editorStateJsonRef.current = null
            setLastSavedAt(null)
            setAutosaveState('idle')
            setDirty(false)
            const freshNamespace = createDraftAssetNamespace()
            localStorage.setItem(LOCAL_DRAFT_ASSET_KEY, freshNamespace)
            setDraftAssetNamespace(freshNamespace)
            showSnackbar(
              'Requested draft was not found. Started a new draft.',
              'warning',
            )
            navigate('/blogs/write', { replace: true })
            return
          }
          // Preserve editorStateJson from localStorage before API data overwrites it
          // (API response never contains editorStateJson — it only lives locally)
          const localSnapshot = readLocalDraftSnapshot(
            myBlog?.id || requestedBlogId,
          )
          const reconciliation = reconcileServerAndLocalDraft(
            myBlog,
            localSnapshot,
          )
          const draftToApply = reconciliation.useLocalDraft
            ? { ...myBlog, ...localSnapshot, id: myBlog.id }
            : myBlog

          applyLocalDraft(draftToApply)

          const effectiveEditorJson = reconciliation.useLocalDraft
            ? localSnapshot?.editorStateJson || null
            : reconciliation.useLocalEditorState
              ? localSnapshot?.editorStateJson || null
              : null
          setInitialEditorStateJson(effectiveEditorJson)
          editorStateJsonRef.current = effectiveEditorJson
          setDraftId(myBlog?.id || null)

          if (myBlog?.id) {
            localStorage.setItem(LOCAL_DRAFT_ASSET_KEY, myBlog.id)
            setDraftAssetNamespace(myBlog.id)
          }
          persistLocalDraft(
            toLocalCachePayload(draftToApply),
            myBlog.id || null,
            {
              editorStateJson: effectiveEditorJson,
              synced: !reconciliation.useLocalDraft,
              serverUpdatedAt: myBlog.updatedDate || null,
              savedAt: reconciliation.useLocalDraft
                ? Number(localSnapshot?.savedAt) || Date.now()
                : Date.now(),
              lastSyncedAt: reconciliation.useLocalDraft
                ? Number(localSnapshot?.lastSyncedAt) || null
                : Date.now(),
            },
          )
          if (reconciliation.notice) {
            showSnackbar(reconciliation.notice, 'info')
          }
          setDirty(false)
          return
        }

        const nextForm = createInitialForm(defaultAuthorName)
        setForm(nextForm)
        formRef.current = nextForm
        setDraftId(null)
        setInitialEditorStateJson(null)
        editorStateJsonRef.current = null
        setLastSavedAt(null)
        setAutosaveState('idle')
        setDirty(false)
        const shouldCreateFreshNamespace =
          isNewLocationVisit ||
          startFresh ||
          !draftAssetNamespaceRef.current ||
          Boolean(draftIdRef.current)
        const nextNamespace = shouldCreateFreshNamespace
          ? createDraftAssetNamespace()
          : draftAssetNamespaceRef.current
        localStorage.setItem(LOCAL_DRAFT_ASSET_KEY, nextNamespace)
        setDraftAssetNamespace(nextNamespace)
      } catch (error) {
        console.error('Failed to initialize blog writer:', error)
      } finally {
        setLoading(false)
      }
    }

    initializeWriter()
  }, [
    applyLocalDraft,
    clearLocalDraftState,
    defaultAuthorName,
    location.key,
    navigate,
    persistLocalDraft,
    readLocalDraftSnapshot,
    reconcileServerAndLocalDraft,
    requestedBlogId,
    showSnackbar,
    toLocalCachePayload,
  ])

  const saveDraft = useCallback(
    async (silent = false, customMessage = null, source = 'manual') => {
      const currentForm = formRef.current
      const activeDraftId = draftIdRef.current
      const payload = {
        title: currentForm.title,
        subtitle: currentForm.subtitle,
        description: currentForm.description,
        excerpt: currentForm.excerpt,
        category: currentForm.category,
        author: currentForm.author,
        imageUrl: currentForm.imageUrl,
        metaKeywords: currentForm.metaKeywords,
        content: currentForm.content,
        editorStateJson: editorStateJsonRef.current,
      }
      const isAutosave = source === 'autosave'
      // Capture the edit version before the async API call so we can tell whether new edits arrived while the request was in flight.
      const versionAtSaveStart = dirtyVersionRef.current
      try {
        if (isAutosave) {
          setAutosaveState('saving')
        }
        setSaving(true)
        const response = await apiService.saveBlogDraft(payload, activeDraftId)
        setDraftId(response.id)
        if (response?.id) {
          localStorage.setItem(LOCAL_DRAFT_ASSET_KEY, response.id)
          setDraftAssetNamespace(response.id)
        }
        // Only clear dirty when no new edits arrived during the API call.
        if (dirtyVersionRef.current === versionAtSaveStart) {
          setDirty(false)
        }
        const savedAt = Date.now()
        persistLocalDraft(
          toLocalCachePayload(response || payload),
          response.id,
          {
            synced: true,
            editorStateJson: editorStateJsonRef.current,
            serverUpdatedAt: response?.updatedDate || null,
            savedAt,
          },
        )
        // Update the ref AFTER persistLocalDraft so the old namespace context is still visible to getCurrentDraftContextId() during cleanup.
        draftIdRef.current = response.id
        setLastSavedAt(savedAt)
        setAutosaveState('saved')
        if (!silent) {
          showSnackbar(customMessage || 'Draft saved successfully')
        }
        return response
      } catch (error) {
        if (activeDraftId && isMissingCurrentUserDraftError(error)) {
          console.warn(
            'Draft reference is stale or deleted. Recreating as new draft.',
            activeDraftId,
          )
          setDraftId(null)
          removeLocalDraftSnapshot(activeDraftId)
          localStorage.removeItem(LOCAL_DRAFT_ASSET_KEY)

          const recreated = await apiService.saveBlogDraft(payload, null)
          setDraftId(recreated?.id || null)
          if (recreated?.id) {
            localStorage.setItem(LOCAL_DRAFT_ASSET_KEY, recreated.id)
            setDraftAssetNamespace(recreated.id)
          }
          if (dirtyVersionRef.current === versionAtSaveStart) {
            setDirty(false)
          }
          const recreatedSavedAt = Date.now()
          persistLocalDraft(
            toLocalCachePayload(recreated || payload),
            recreated?.id || null,
            {
              synced: true,
              editorStateJson: editorStateJsonRef.current,
              serverUpdatedAt: recreated?.updatedDate || null,
              savedAt: recreatedSavedAt,
            },
          )
          // Update the ref AFTER persistLocalDraft for the same reason.
          draftIdRef.current = recreated?.id || null
          setLastSavedAt(recreatedSavedAt)
          setAutosaveState('saved')
          if (!silent) {
            showSnackbar(
              'Previous draft was removed. Saved as a new draft.',
              'info',
            )
          }
          return recreated
        }

        console.error('Failed to save draft:', error)
        if (isAutosave) {
          setAutosaveState('error')
        }
        if (!silent) {
          showSnackbar(
            getBackendErrorMessage(error) || 'Failed to save draft',
            'error',
          )
        }
        throw error
      } finally {
        setSaving(false)
      }
    },
    [
      getBackendErrorMessage,
      isMissingCurrentUserDraftError,
      persistLocalDraft,
      removeLocalDraftSnapshot,
      showSnackbar,
      toLocalCachePayload,
    ],
  )

  useEffect(() => {
    if (!dirty || submitting) return
    setAutosaveState('pending')
    clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      saveDraft(true, null, 'autosave').catch(() => {})
    }, 8000)
    return () => clearTimeout(autosaveTimerRef.current)
  }, [form, dirty, saveDraft, submitting])

  const updateField = useCallback(
    (key, value) => {
      setForm((prev) => {
        const next = { ...prev, [key]: value }
        formRef.current = next
        persistLocalDraft(next, draftId, { synced: false })
        return next
      })
      setFormErrors((prev) => {
        if (!prev[key]) {
          return prev
        }
        const next = { ...prev }
        delete next[key]
        return next
      })
      dirtyVersionRef.current += 1
      setDirty(true)
      setAutosaveState('pending')
    },
    [draftId, persistLocalDraft],
  )

  const normalizeCategory = useCallback(
    (rawCategory) => (rawCategory || '').trim().replace(/\s+/g, ' '),
    [],
  )

  const addCategoryOptionIfMissing = useCallback((nextCategory) => {
    if (!nextCategory) {
      return
    }
    setCategories((previous) => {
      const exists = previous.some(
        (option) => option.toLowerCase() === nextCategory.toLowerCase(),
      )
      if (exists) {
        return previous
      }
      return [...previous, nextCategory].sort((a, b) => a.localeCompare(b))
    })
  }, [])

  const commitCategory = useCallback(
    (rawCategory) => {
      const nextCategory = normalizeCategory(rawCategory)
      setCategoryInputValue(nextCategory)
      updateField('category', nextCategory)
      addCategoryOptionIfMissing(nextCategory)
    },
    [addCategoryOptionIfMissing, normalizeCategory, updateField],
  )

  const readImageDimensions = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const image = new Image()
        const objectUrl = URL.createObjectURL(file)
        image.onload = () => {
          const dimensions = { width: image.width, height: image.height }
          URL.revokeObjectURL(objectUrl)
          resolve(dimensions)
        }
        image.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          reject(new Error('Unable to read image dimensions.'))
        }
        image.src = objectUrl
      }),
    [],
  )

  const validateCoverImageFile = useCallback(
    async (file) => {
      if (!file) {
        throw new Error('Cover image file is required.')
      }
      if (!COVER_ALLOWED_TYPES.includes(file.type)) {
        throw new Error(
          'Only JPG, PNG, and WEBP images are allowed for cover image.',
        )
      }
      if (file.size > COVER_MAX_IMAGE_SIZE_BYTES) {
        throw new Error('Cover image must be 10MB or smaller.')
      }
      try {
        const { width, height } = await readImageDimensions(file)
        if (width < COVER_MIN_WIDTH || height < COVER_MIN_HEIGHT) {
          showSnackbar(
            `Recommended minimum is ${COVER_MIN_WIDTH}x${COVER_MIN_HEIGHT}px for best rendering. Uploading anyway.`,
            'warning',
          )
        }
      } catch (error) {
        console.warn('Unable to validate cover image dimensions:', error)
        showSnackbar(
          'Could not verify cover image dimensions. Uploading anyway.',
          'warning',
        )
      }
    },
    [readImageDimensions, showSnackbar],
  )

  const resolveUploadScope = useCallback(() => {
    let effectiveDraftNamespace = draftAssetNamespace
    if (!draftId && !effectiveDraftNamespace) {
      effectiveDraftNamespace = createDraftAssetNamespace()
      localStorage.setItem(LOCAL_DRAFT_ASSET_KEY, effectiveDraftNamespace)
      setDraftAssetNamespace(effectiveDraftNamespace)
    }
    return draftId
      ? { blogId: draftId }
      : { draftId: effectiveDraftNamespace || createDraftAssetNamespace() }
  }, [draftId, draftAssetNamespace])

  const uploadInlineImage = useCallback(
    async (file) => {
      if (!file) {
        return ''
      }
      try {
        setUploadingInlineImage(true)
        const response = await apiService.uploadBlogImage(file, {
          ...resolveUploadScope(),
          assetType: 'INLINE',
        })
        if (response?.url) {
          return response.url
        }
        showSnackbar('Inline image upload failed', 'error')
        return ''
      } catch (error) {
        console.error('Inline image upload failed:', error)
        showSnackbar('Inline image upload failed', 'error')
        return ''
      } finally {
        setUploadingInlineImage(false)
      }
    },
    [resolveUploadScope, showSnackbar],
  )

  const handleCoverImageUpload = useCallback(
    async (event) => {
      const file = event.target.files?.[0]
      if (!file) return
      try {
        setUploadingCoverImage(true)
        await validateCoverImageFile(file)
        const response = await apiService.uploadBlogImage(file, {
          ...resolveUploadScope(),
          assetType: 'COVER',
        })
        if (response?.url) {
          updateField('imageUrl', response.url)
          setCoverLocalPreviewUrl((previous) => {
            if (previous && previous.startsWith('blob:')) {
              URL.revokeObjectURL(previous)
            }
            return ''
          })
          showSnackbar('Cover image uploaded successfully')
        }
      } catch (error) {
        console.error('Cover image upload failed:', error)
        const message =
          error?.message ||
          error?.response?.data?.message ||
          'Cover image upload failed. Try a JPG/PNG/WEBP image.'
        showSnackbar(message, 'error')
      } finally {
        setUploadingCoverImage(false)
        event.target.value = ''
      }
    },
    [resolveUploadScope, showSnackbar, updateField, validateCoverImageFile],
  )

  const handleMetaKeywordKeyDown = (event) => {
    if (event.key === 'Enter' && keywordInput.trim()) {
      event.preventDefault()
      if (!form.metaKeywords.includes(keywordInput.trim())) {
        updateField('metaKeywords', [...form.metaKeywords, keywordInput.trim()])
      }
      setKeywordInput('')
    }
  }

  const validateForReview = useCallback(() => {
    const errors = {}
    const title = (form.title || '').trim()
    const subtitle = form.subtitle || ''
    const description = (form.description || '').trim()
    const category = (form.category || '').trim()

    if (!title) {
      errors.title = 'Title is required before submitting for review.'
    } else if (title.length > TITLE_MAX_CHARS) {
      errors.title = `Title must be ${TITLE_MAX_CHARS} characters or less.`
    }

    if (subtitle.length > SUBTITLE_MAX_CHARS) {
      errors.subtitle = `Subtitle must be ${SUBTITLE_MAX_CHARS} characters or less.`
    }

    if (!description) {
      errors.description =
        'Description is required before submitting for review.'
    } else if (description.length > DESCRIPTION_MAX_CHARS) {
      errors.description = `Description must be ${DESCRIPTION_MAX_CHARS} characters or less.`
    }

    if (!category) {
      errors.category = 'Category is required before submitting for review.'
    }

    if (contentTextLength < MIN_CONTENT_CHARS_FOR_REVIEW) {
      errors.content = `Content must be at least ${MIN_CONTENT_CHARS_FOR_REVIEW} characters before submitting for review.`
    }

    return errors
  }, [
    contentTextLength,
    form.category,
    form.description,
    form.subtitle,
    form.title,
  ])

  const mapSubmitErrorToField = useCallback((message) => {
    const normalized = (message || '').toLowerCase()
    if (normalized.includes('title')) return 'title'
    if (normalized.includes('subtitle')) return 'subtitle'
    if (normalized.includes('description')) return 'description'
    if (normalized.includes('category')) return 'category'
    if (normalized.includes('content')) return 'content'
    return ''
  }, [])

  const handleSaveDraftAndReturn = async () => {
    clearTimeout(autosaveTimerRef.current)
    try {
      await saveDraft(false)
      sessionStorage.setItem('blog_writer_notice', 'Draft saved successfully')
      navigate('/blogs?section=team', {
        state: {
          blogWriterMessage: 'Draft saved successfully',
        },
      })
    } catch (error) {
      console.error('Save draft and redirect failed:', error)
    }
  }

  const handleSubmitForReview = async () => {
    clearTimeout(autosaveTimerRef.current)
    const validationErrors = validateForReview()
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors)
      showSnackbar(
        validationErrors.title ||
          validationErrors.description ||
          validationErrors.category ||
          validationErrors.content ||
          'Please fix validation errors before submitting.',
        'error',
      )
      return
    }

    try {
      setSubmitting(true)
      const saved = await saveDraft(true)
      const effectiveDraftId = saved?.id || draftId
      if (!effectiveDraftId) {
        showSnackbar('Save draft first before submit', 'error')
        return
      }
      await apiService.submitBlogForReview(effectiveDraftId)
      clearTimeout(autosaveTimerRef.current)
      setDirty(false)
      clearLocalDraftState(effectiveDraftId)
      setDraftId(null)
      setDraftAssetNamespace('')
      sessionStorage.setItem('blog_writer_start_fresh', 'true')
      sessionStorage.setItem(
        'blog_writer_notice',
        'Your blog has been submitted for review',
      )
      navigate('/blogs?section=team', {
        state: {
          blogWriterMessage: 'Your blog has been submitted for review',
        },
      })
    } catch (error) {
      console.error('Submit for review failed:', error)
      const backendMessage = getBackendErrorMessage(error)
      const field = mapSubmitErrorToField(backendMessage)
      if (field) {
        setFormErrors((prev) => ({
          ...prev,
          [field]: backendMessage,
        }))
      }
      showSnackbar(backendMessage || 'Failed to submit for review', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleLeaveWithoutSave = () => {
    setLeaveDialogOpen(false)
    if (blockedTx) {
      blockedTx.retry()
      setBlockedTx(null)
    }
  }

  const handleSaveAndLeave = async () => {
    clearTimeout(autosaveTimerRef.current)
    await saveDraft(true).catch(() => {})
    setLeaveDialogOpen(false)
    if (blockedTx) {
      blockedTx.retry()
      setBlockedTx(null)
    }
  }

  if (loading) {
    return (
      <Container maxWidth='md' sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    )
  }

  return (
    <Container maxWidth='lg' sx={{ py: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant='h4'
          sx={{ color: themeColors.primary, fontWeight: 700 }}
        >
          Write a Blog
        </Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
          Draft freely. Save anytime. Submit when ready for admin review.
        </Typography>
      </Box>

      <Stack spacing={2}>
        <TextField
          label='Title'
          value={form.title}
          onChange={(e) => updateField('title', e.target.value)}
          error={Boolean(formErrors.title)}
          helperText={
            formErrors.title ||
            `Required for review. ${form.title.length}/${TITLE_MAX_CHARS}`
          }
          inputProps={{ maxLength: TITLE_MAX_CHARS }}
          fullWidth
          sx={themedTextFieldSx}
        />
        <TextField
          label='Subtitle'
          value={form.subtitle}
          onChange={(e) => updateField('subtitle', e.target.value)}
          error={Boolean(formErrors.subtitle)}
          helperText={
            formErrors.subtitle ||
            `Optional. ${form.subtitle.length}/${SUBTITLE_MAX_CHARS}`
          }
          inputProps={{ maxLength: SUBTITLE_MAX_CHARS }}
          fullWidth
          sx={themedTextFieldSx}
        />
        <TextField
          label='Description'
          multiline
          minRows={2}
          value={form.description}
          onChange={(e) => updateField('description', e.target.value)}
          error={Boolean(formErrors.description)}
          helperText={
            formErrors.description ||
            `Required for review. ${form.description.length}/${DESCRIPTION_MAX_CHARS}`
          }
          inputProps={{ maxLength: DESCRIPTION_MAX_CHARS }}
          fullWidth
          sx={themedTextFieldSx}
        />
        <TextField
          label='Excerpt'
          multiline
          minRows={2}
          value={form.excerpt}
          onChange={(e) => updateField('excerpt', e.target.value)}
          fullWidth
          sx={themedTextFieldSx}
        />
        <Autocomplete
          freeSolo
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          options={categories}
          value={form.category || ''}
          inputValue={categoryInputValue}
          filterOptions={(options, params) => {
            const filtered = categoryFilterOptions(options, params)
            const normalizedInput = normalizeCategory(params.inputValue || '')
            const exists = options.some(
              (option) =>
                option.toLowerCase() === normalizedInput.toLowerCase(),
            )
            if (normalizedInput && !exists) {
              filtered.push(`${ADD_CATEGORY_TOKEN}${normalizedInput}`)
            }
            return filtered
          }}
          getOptionLabel={(option) =>
            option.startsWith(ADD_CATEGORY_TOKEN)
              ? option.slice(ADD_CATEGORY_TOKEN.length)
              : option
          }
          renderOption={(props, option) => {
            const isAddOption = option.startsWith(ADD_CATEGORY_TOKEN)
            const label = isAddOption
              ? `Add "${option.slice(ADD_CATEGORY_TOKEN.length)}"`
              : option
            return <li {...props}>{label}</li>
          }}
          onChange={(event, value) => {
            if (typeof value !== 'string') {
              commitCategory('')
              return
            }
            const selectedCategory = value.startsWith(ADD_CATEGORY_TOKEN)
              ? value.slice(ADD_CATEGORY_TOKEN.length)
              : value
            commitCategory(selectedCategory)
          }}
          onInputChange={(event, value, reason) => {
            if (reason === 'reset') {
              return
            }
            const nextValue = value || ''
            setCategoryInputValue(nextValue)
            if (reason === 'input') {
              updateField('category', nextValue)
            }
            if (reason === 'clear') {
              commitCategory('')
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Category'
              error={Boolean(formErrors.category)}
              helperText={
                formErrors.category ||
                'Required for review. Select existing category or type a new one and press Enter.'
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter' && categoryInputValue.trim()) {
                  event.preventDefault()
                  commitCategory(categoryInputValue)
                }
              }}
              sx={themedTextFieldSx}
            />
          )}
        />
        <TextField
          label='Author Name (optional)'
          value={form.author}
          onChange={(e) => updateField('author', e.target.value)}
          sx={themedTextFieldSx}
        />

        <Box
          sx={{
            border: '1px dashed #bfc8d8',
            borderRadius: 2,
            p: 2,
            backgroundColor: '#fbfcff',
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{ color: themeColors.primary, fontWeight: 700 }}
          >
            Cover Image
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant='caption'
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              Supported formats: JPG, PNG, or WEBP (Max 10MB)
            </Typography>

            <Typography
              variant='caption'
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              Recommended dimensions: 1600x900 (16:9 aspect ratio)
            </Typography>

            <Typography
              variant='caption'
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              Recommended minimum: {COVER_MIN_WIDTH} x {COVER_MIN_HEIGHT} px
            </Typography>

            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontStyle: 'italic',
              }}
            >
              Local preview appears immediately after selection.
            </Typography>
          </Box>
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{ mt: 1.25 }}
          >
            <Button
              variant='outlined'
              startIcon={
                uploadingCoverImage ? (
                  <CircularProgress size={16} />
                ) : (
                  <AddPhotoAlternate />
                )
              }
              onClick={() => coverImageInputRef.current?.click()}
              disabled={uploadingCoverImage || saving || submitting}
              sx={{
                borderColor: themeColors.primary,
                color: themeColors.primary,
                textTransform: 'none',
                '&:hover': {
                  borderColor: themeColors.hover,
                  color: themeColors.hover,
                },
              }}
            >
              {form.imageUrl ? 'Replace Cover' : 'Upload Cover'}
            </Button>
            {form.imageUrl && (
              <Button
                variant='text'
                color='error'
                startIcon={<DeleteOutline />}
                onClick={() => {
                  updateField('imageUrl', '')
                  setCoverLocalPreviewUrl((previous) => {
                    if (previous && previous.startsWith('blob:')) {
                      URL.revokeObjectURL(previous)
                    }
                    return ''
                  })
                }}
                sx={{ textTransform: 'none' }}
              >
                Remove
              </Button>
            )}
          </Stack>
          <input
            ref={coverImageInputRef}
            type='file'
            accept={COVER_ALLOWED_TYPES.join(',')}
            style={{ display: 'none' }}
            onChange={handleCoverImageUpload}
          />
          {form.imageUrl && (
            <Box
              sx={{
                mt: 2,
                width: '100%',
                minHeight: { xs: 170, sm: 220, md: 'auto' },
                borderRadius: 1.5,
                border: '1px solid #dbe2ef',
                backgroundColor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: { xs: 'hidden', md: 'visible' },
              }}
            >
              <Box
                component='img'
                src={form.imageUrl}
                alt='Cover preview'
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: { xs: 280, sm: 340, md: 'none' },
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>
          )}
        </Box>

        <TextField
          label='Add Meta Keyword'
          placeholder='e.g. UAE VAT registration threshold'
          value={keywordInput}
          onChange={(e) => setKeywordInput(e.target.value)}
          onKeyDown={handleMetaKeywordKeyDown}
          helperText='Press Enter to add each keyword(s) to improve SEO for this blog post.'
          sx={themedTextFieldSx}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {form.metaKeywords.map((keyword) => (
            <Chip
              key={keyword}
              label={keyword}
              sx={{
                backgroundColor: themeColors.secondary,
                color: themeColors.primary,
                '& .MuiChip-deleteIcon': { color: themeColors.primary },
              }}
              onDelete={() =>
                updateField(
                  'metaKeywords',
                  form.metaKeywords.filter((item) => item !== keyword),
                )
              }
            />
          ))}
        </Box>
        <LexicalBlogEditor
          value={form.content}
          editorStateJson={initialEditorStateJson}
          onChange={(value) => updateField('content', value)}
          onEditorStateJsonChange={(json) => {
            editorStateJsonRef.current = json
            persistLocalDraft(formRef.current, draftId, {
              synced: false,
              editorStateJson: json,
            })
          }}
          onUploadImage={uploadInlineImage}
          isUploadingImage={uploadingInlineImage}
          disabled={saving || submitting}
          onNotify={showSnackbar}
          contentError={formErrors.content}
          contentStatusMessage=''
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: { md: 'space-between' },
            gap: 1,
          }}
        >
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {contentReviewStatusText}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: autosaveState === 'error' ? '#b42318' : '#1e4b8f',
              backgroundColor:
                autosaveState === 'error' ? '#fff2f0' : '#f2f7ff',
              border: `1px solid ${autosaveState === 'error' ? '#fecdca' : '#c7d7f9'}`,
              borderRadius: 1.5,
              px: 1.5,
              py: 0.75,
              display: 'inline-flex',
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
            {autosaveStatusText}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent='flex-end'
        >
          <Button
            variant='outlined'
            startIcon={<Visibility />}
            onClick={() => setPreviewOpen(true)}
            sx={{
              borderColor: themeColors.primary,
              color: themeColors.primary,
              textTransform: 'none',
            }}
          >
            Preview
          </Button>
          <Button
            variant='outlined'
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            disabled={saving || submitting}
            onClick={handleSaveDraftAndReturn}
            sx={{
              borderColor: themeColors.primary,
              color: themeColors.primary,
              textTransform: 'none',
            }}
          >
            {saving
              ? autosaveState === 'saving'
                ? 'Auto-saving...'
                : 'Saving...'
              : 'Save Draft'}
          </Button>
          <Button
            variant='contained'
            startIcon={
              submitting ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                <Send />
              )
            }
            disabled={saving || submitting}
            onClick={handleSubmitForReview}
            sx={{
              backgroundColor: themeColors.primary,
              color: 'white',
              textTransform: 'none',
              '&:hover': { backgroundColor: themeColors.hover },
            }}
          >
            {submitting ? 'Submitting...' : 'Submit for Review'}
          </Button>
        </Stack>
      </Stack>

      <PreviewBlogModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={form.title}
        category={form.category}
        author={form.author}
        content={form.content}
        showSnackbar={showSnackbar}
      />

      <Dialog
        open={leaveDialogOpen}
        onClose={() => {
          setLeaveDialogOpen(false)
          setBlockedTx(null)
        }}
      >
        <DialogTitle>Unsaved Draft</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Save this draft before leaving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLeaveDialogOpen(false)
              setBlockedTx(null)
            }}
          >
            Keep Editing
          </Button>
          <Button onClick={handleLeaveWithoutSave} color='error'>
            Leave Without Saving
          </Button>
          <Button onClick={handleSaveAndLeave} variant='contained'>
            Save Draft & Leave
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default BlogWriter
